import React, { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';

export interface AnimatedTab<T extends string | number> {
  label: string;
  value: T;
  panelId?: string;
  id?: string;
}

export interface AnimatedTabsProps<T extends string | number> {
  tabs: AnimatedTab<T>[];
  value: T;
  onChange: (event: React.SyntheticEvent, value: T) => void;
  label: string;
  className?: string;
}

/** Controlled tab strip with a sliding selection surface. Panel ownership stays with the caller. */
export function AnimatedTabs<T extends string | number>({
  tabs,
  value,
  onChange,
  label,
  className,
}: AnimatedTabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);
  const activeIndex = tabs.findIndex((tab) => tab.value === value);

  useLayoutEffect(() => {
    const list = listRef.current;
    const updateIndicator = () => {
      const button = buttonRefs.current[activeIndex];
      if (!list || !button) return;
      setIndicator({ left: button.offsetLeft, width: button.offsetWidth });
    };

    updateIndicator();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateIndicator);
    if (list && observer) observer.observe(list);
    window.addEventListener('resize', updateIndicator);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateIndicator);
    };
  }, [activeIndex, tabs.length]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (tabs.length < 2) return;
    let nextIndex: number;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = tabs.length - 1;
    else return;

    event.preventDefault();
    buttonRefs.current[nextIndex]?.focus();
    onChange(event, tabs[nextIndex].value);
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      className={cn(
        'relative inline-flex shrink-0 items-center gap-[2px] rounded-xl border border-border bg-surface-2 p-[4px] shadow-sm',
        className
      )}
    >
      {indicator && (
        <span
          aria-hidden="true"
          data-testid="animated-tab-indicator"
          className="seg-on pointer-events-none absolute left-0 top-[4px] bottom-[4px] rounded-[10px] transition-[transform,width] duration-300 ease-out motion-reduce:transition-none after:absolute after:inset-x-3 after:bottom-[3px] after:h-[2px] after:rounded-full after:bg-cyan after:content-['']"
          style={{ width: indicator.width, transform: `translateX(${indicator.left}px)` }}
        />
      )}
      {tabs.map((tab, index) => {
        const active = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(node) => {
              buttonRefs.current[index] = node;
            }}
            id={tab.id}
            type="button"
            role="tab"
            aria-controls={tab.panelId}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={(event) => onChange(event, tab.value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            className={cn(
              'relative z-10 inline-flex min-h-[30px] cursor-pointer appearance-none items-center justify-center whitespace-nowrap rounded-[10px] border-0 bg-transparent px-3 py-1.5 text-[12.5px] font-semibold transition-colors duration-200 sm:min-h-[32px] sm:px-4 sm:text-[13px] motion-reduce:transition-none',
              active ? 'text-white' : 'text-muted hover:bg-surface-hi/40 hover:text-body',
              active && !indicator && 'seg-on'
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
